import { supabase } from '@/integrations/supabase/client';

export interface RecordingMetadata {
  streamId: string;
  creatorId: string;
  title: string;
  description?: string;
  duration: number;
  thumbnailUrl?: string;
  publishRecording?: boolean;
}

export async function uploadRecording(
  videoBlob: Blob,
  metadata: RecordingMetadata
): Promise<string> {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const filename = `${metadata.creatorId}/${timestamp}.webm`;

    console.log('Uploading recording:', filename, 'Size:', videoBlob.size);

    // Upload video file to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('recordings')
      .upload(filename, videoBlob, {
        contentType: 'video/webm',
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      throw uploadError;
    }

    // Get public URL for the video
    const { data: { publicUrl } } = supabase.storage
      .from('recordings')
      .getPublicUrl(filename);

    // Generate and upload thumbnail
    let thumbnailUrl = metadata.thumbnailUrl;
    try {
      const thumbnailBlob = await generateThumbnail(videoBlob);
      const thumbnailPath = `${metadata.creatorId}/${timestamp}.jpg`;
      
      const { error: thumbError } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailPath, thumbnailBlob, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
        });

      if (!thumbError) {
        const { data: { publicUrl: thumbUrl } } = supabase.storage
          .from('thumbnails')
          .getPublicUrl(thumbnailPath);
        thumbnailUrl = thumbUrl;
      } else {
        console.error('Thumbnail upload error:', thumbError);
      }
    } catch (thumbGenError) {
      console.error('Thumbnail generation error:', thumbGenError);
    }

    // Determine if recording should be published
    const shouldPublish = metadata.publishRecording !== false;
    
    // Create recording entry in database
    const { data, error } = await supabase
      .from('recordings')
      .insert({
        stream_id: metadata.streamId,
        creator_id: metadata.creatorId,
        title: metadata.title,
        description: metadata.description,
        duration: metadata.duration,
        file_size: videoBlob.size,
        thumbnail_url: thumbnailUrl,
        video_url: publicUrl,
        status: 'completed',
        is_published: shouldPublish,
        published_at: shouldPublish ? new Date().toISOString() : null,
      })
      .select()
      .single();

    if (error) throw error;

    if (shouldPublish) {
      console.log('Recording published to discovery:', data.id);
    } else {
      console.log('Recording saved but not published:', data.id);
    }
    return data.id;
  } catch (error) {
    console.error('Error uploading recording:', error);
    throw error;
  }
}

export async function generateThumbnail(videoBlob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(2, video.duration / 2); // Capture at 2s or midpoint
    };

    video.onseeked = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx?.drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate thumbnail'));
        }
      }, 'image/jpeg', 0.8);
    };

    video.onerror = () => reject(new Error('Video load error'));
    video.src = URL.createObjectURL(videoBlob);
  });
}
